import { motion } from "framer-motion";
import SafeIcon from "./SafeIcon";
import type { ConverterTheme, HeroFeature } from "./types";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

interface HeroProps {
  badge: string;
  title: string;
  highlightedTitle: string;
  description: string;
  detail: string;
  features: HeroFeature[];
  theme: ConverterTheme;
}

const Hero = ({
  badge,
  title,
  highlightedTitle,
  description,
  detail,
  features,
  theme,
}: HeroProps) => (
  <section
    aria-label="Introduction"
    className="mx-auto max-w-3xl px-4 py-14 text-center"
  >
    <motion.div variants={container} initial="hidden" animate="show">
      <motion.div variants={item}>
        <span
          className={`mb-6 inline-block rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest ${theme.accentSoftPill}`}
        >
          {badge}
        </span>
      </motion.div>
      <motion.h1
        variants={item}
        className="mb-5 text-balance text-4xl font-black leading-tight tracking-tight text-slate-900 md:text-6xl dark:text-white"
      >
        {title}{" "}
        <span
          className={`bg-gradient-to-r bg-clip-text text-transparent ${theme.logoText}`}
        >
          {highlightedTitle}
        </span>
      </motion.h1>
      <motion.p
        variants={item}
        className="mx-auto mb-4 max-w-2xl text-lg leading-relaxed text-slate-500 md:text-xl dark:text-slate-400"
      >
        {description}
      </motion.p>
      <motion.p
        variants={item}
        className="mx-auto mb-10 max-w-xl text-sm text-slate-400 dark:text-slate-500"
      >
        {detail}
      </motion.p>
      <motion.div
        variants={item}
        className="flex flex-wrap justify-center gap-3"
      >
        {features.map(({ icon, label }) => (
          <div
            key={label}
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300"
          >
            <SafeIcon icon={icon} className={`${theme.featureIcon} shrink-0`} />
            <span>{label}</span>
          </div>
        ))}
      </motion.div>
    </motion.div>
  </section>
);

export default Hero;
